import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray, FormControl } from '@angular/forms';
import { slideInDownAnimation } from '../../app/app.animation';
import { ErrorParseService, IMatchingGift, LGEDataService, PermissionsService } from '../../commons';
import { Store } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';
import * as fromMatchingGift from './state/matching-gift.selectors';
import * as MatchingGiftAction from './state/matching-gift.actions';
import * as lgeApp from '../../app/state/app.actions';
import { createNumberMask } from 'text-mask-addons';
import { Router, ActivatedRoute, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { catchError, map, skipWhile, tap } from 'rxjs/operators';
import { PreviousRouteService } from 'src/commons/services/previousRoute.service';
import { AngularMaterialService } from 'src/commons/utilities/angular.material.service';
import { saveAs } from 'file-saver';

@Component({
  selector: 'matching-gift-screen',
  templateUrl: './matching-gift.component.html',
  styleUrls: ['./matching-gift.component.scss'],
  animations: [slideInDownAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class MatchingGiftScreenComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() private pending$: Observable<boolean>;
  private pendingSubscription: Subscription;
  private pending: boolean;
  private pendingMsg = "Loading...";
  @Input() private error$: Observable<string | null>;
  private errorSubscription: Subscription;
  private error: string | null;
  @Input() private success$: Observable<string | null>;
  private successSubscription: Subscription;
  private success: string | null;
  @Input() private stateData$: Observable<IMatchingGift | null>;
  private stateDataSubscription: Subscription;
  mgForm: FormGroup;
  formId;
  isEINValidated = false;
  isMaxLength = false;
  isReadOnly = false;
  companies = ['LG&E', 'KU', 'ODP', 'LKS'];
  states = ['AK', 'AL', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY',
    'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK',
    'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
  titles = ['Dr.', 'Mr.', 'Mrs.', 'Ms.'];

  @ViewChild('fileInput1') fileInput1: ElementRef;

  private currencyMask = createNumberMask({
    prefix: '',
    suffix: '',
    includeThousandsSeparator: false,
    thousandsSeparatorSymbol: '',
    allowDecimal: true,
    decimalSymbol: '.',
    decimalLimit: 2,
    integerLimit: null,
    requireDecimal: false,
    allowNegative: false,
    allowLeadingZeroes: false
  });

  attachmentDeleted = this.fb.array([]);
  formData: any;
  minEffdt = new Date('');
  isShowSaveAsDraft = false;
  isApprover = false;
  isShowLeaveDialog = true;
  taxExcemptList = [];

  constructor(
    private store: Store<fromMatchingGift.State>,
    private ref: ChangeDetectorRef,
    private fb: FormBuilder,
    private ams: AngularMaterialService,
    private errorParse: ErrorParseService,
    private _dataService: LGEDataService,
    private router: Router,
    private route: ActivatedRoute,
    private permissionsService: PermissionsService,
    private previousRouteService: PreviousRouteService
  ) {
    this.selectState();
    this.createForms();
    this._dataService.GetTaxExemptSubsections().subscribe(
      {
        next: data => {
          this.taxExcemptList = data.map(s => s.subsection);
        }
      }
    )
  }

  ngOnInit() {
    this.ref.markForCheck();
    this.route.params.subscribe((response) => {
      this.formId = parseInt(response['id']);
    });
  }

  ngAfterViewInit(): void {
    this.startSubscriptions();
    if (this.formId) {
      setTimeout(() => {
        this.store.dispatch(new MatchingGiftAction.LoadMatchingGiftState(this.formId));
      })
    } else {
      this.isReadOnly = !this.hasRole('ADMIN');
      this.isShowSaveAsDraft = this.hasRole('ADMIN');
    }
  }
  // Select state
  private selectState(): void {
    this.pending$ = this.store.select(fromMatchingGift.getMatchingGiftScreenPending);
    this.stateData$ = this.store.select(fromMatchingGift.getMatchingGiftState).pipe(skipWhile(state => state === null || state === undefined));
    this.error$ = this.store.select(fromMatchingGift.getMatchingGiftScreenError).pipe(skipWhile((error) => error === undefined || error === null || error === ''));
    this.success$ = this.store.select(fromMatchingGift.getMatchingGiftScreenSuccess).pipe(skipWhile(success => success === null || success === undefined));
  }

  private displayLoader(pending: boolean): void {
    if (pending) {
      this.ams.showLoader(this.pendingMsg);
    } else {
      this.ams.closeDialog().then(() => {
        if (this.error !== null && this.error !== undefined && this.error !== '') {
          this.displayError(this.error);
        } else if (this.success !== null && this.success !== undefined) {
          this.displaySuccess(this.success);
        }
        this.pendingMsg = "Loading...";
      });
    }
  }

  private displayError(error: any): void {
    let errorMessage;
    if (this.errorParse.foundKeyWord(error, "CORPCOMMONWS")) {
      errorMessage = this.errorParse.translateCCError(error);
    } else {
      errorMessage = error instanceof Object ? error.message : error;
    }
    this.ams.showAlert("Loading Error", errorMessage);
    this.ams.alert.afterClosed().subscribe(result => {
      this.store.dispatch(new MatchingGiftAction.MatchingGiftScreenErrorReset());
    });
  }

  private displaySuccess(successMessage: string): void {
    this.isShowLeaveDialog = false;
    this.ams.showAlert('Success!', successMessage);
    let leaving = setTimeout(() => {
      this.toHome();
    }, 5000);
    this.ams.alert.afterClosed().subscribe(() => {
      this.toHome();
      clearTimeout(leaving);
    })
    this.ref.markForCheck();
  }

  toHome() {
    const previousUrl = this.previousRouteService.getPreviousUrl();
    if (!previousUrl?.includes('admin')) {
      this.router.navigate(['']);
    } else {
      this.router.navigate([previousUrl]);
    }
  }
  // Create reactive forms
  private createForms(): void {
    this.mgForm = this.fb.group({
      DonorInfo: this.fb.group({
        donorInfoId: [null],
        EmpId: ['', [Validators.required, Validators.maxLength(7)]],
        title: [''],
        department: [''],
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        phone: ['', Validators.pattern(new RegExp(/^\d{3}-\d{3}-\d{4}/))],
        empStatus: ['FT', Validators.required],
        company: ['', Validators.required],
      }),
      OrgInfo: this.fb.group({
        EIN: ['', [Validators.required, Validators.maxLength(10), Validators.pattern(new RegExp(/^\d{2}-\d{7}/))]],
        orgInfoId: [null],
        name: ['', Validators.required],
        payToOption: ['No'],
        payTo: ['', Validators.maxLength(32)],
        address1: ['', [Validators.required, Validators.maxLength(32)]],
        address2: ['', [Validators.maxLength(32)]],
        city: ['', [Validators.required, Validators.maxLength(25)]],
        state: ['', Validators.required],
        zipCode: ['', [Validators.required, Validators.maxLength(5), Validators.pattern(new RegExp(/^\d{5}$/))]],
        webAddress: [''],
        subsection: ['']
      }),
      formId: [null],
      statusId: [null],
      payment: ['CHECK', Validators.required],
      amount: ['', Validators.required],
      donationVehicle: ['CHECK', Validators.required],
      giftDate: [new Date(), Validators.required],
      attachment: this.fb.array([]),
      submittedDate: [new Date()],
      userId: ['']
    });

    this.mgForm.get(['OrgInfo', 'name']).valueChanges.subscribe((value) => {
      if (value.length > 32) {
        this.isMaxLength = true;
        this.mgForm.get(['OrgInfo', 'payToOption']).setValue('Yes');
        this.mgForm.get(['OrgInfo', 'payTo']).setValue(value.substring(0, 32));
      } else {
        this.isMaxLength = false;
        this.mgForm.get(['OrgInfo', 'payToOption']).setValue('No');
        this.mgForm.get(['OrgInfo', 'payTo']).setValue('');
      }
    });
  }
  // Opens sidenav if on mobile
  public openSidenav(): void {
    this.store.dispatch(new lgeApp.OpenSidenav());
  }

  private createAttachment(base64, name, size): FormGroup {
    return this.fb.group({
      attachId: [-1],
      formId: [-1],
      fileId: [0],
      attachString: [base64],
      attachName: [name],
      attachSize: [size],
      attachType: [''],
      deleteIt: [0]
    });
  }

  public addFormLogInfo(): void {
    this.mgForm.addControl('FormMGLogInfo',
      this.fb.group({
        action: [''],
        comment: [''],
        effdt: [''],
        empId: [''],
        empName: [''],
        empTypeForLog: [''],
        formId: [null],
        formLogId: [null],
        title: [''],
        status: [''],
      }))
  }

  // Subscribe to state data and mark it for changes
  private startSubscriptions(): void {
    this.pendingSubscription = this.pending$
      .subscribe(data => {
        this.pending = data;
        this.displayLoader(this.pending);
        this.ref.markForCheck();
      });
    this.errorSubscription = this.error$
      .subscribe(data => {
        this.error = data;
        this.ref.markForCheck();
      });
    this.successSubscription = this.success$
      .subscribe(data => {
        this.success = data;
        this.ref.markForCheck();
      });
    this.stateDataSubscription = this.stateData$
      .subscribe(data => {
        this.formData = JSON.parse(JSON.stringify(data));
        if (this.formId === this.formData.formId) {
          while ((<FormArray>this.mgForm.controls['attachment']).length > 0) {
            (<FormArray>this.mgForm.controls['attachment']).removeAt(0);
          }
          const AttachLength = this.formData.attachment.length;
          for (let i = 0; i < AttachLength; i++) {
            (<FormArray>this.mgForm.controls['attachment']).push(this.createAttachment('', '', '0'));
          }
          if (this.formData.FormMGLogInfo !== null && this.formData.FormMGLogInfo !== undefined && this.formData.statusId === 11) {
            this.addFormLogInfo();
            this.isApprover = this.formData.FormMGLogInfo.empId === this.permissionsService.getUserId();
          } else {
            delete this.formData.FormMGLogInfo;
          }
          this.mgForm.setValue(this.formData);
          this.mgForm.get('submittedDate').setValue(new Date(this.formData.submittedDate));
          this.isEINValidated = true;
          switch (this.formData.statusId) {
            case 2:
            case 7:
              this.isReadOnly = !this.hasRole('ADMIN');
              this.isShowSaveAsDraft = this.hasRole('ADMIN');
              this.isShowLeaveDialog = true;
              break;
            case 5:
            case 23:
              this.isReadOnly = !this.hasRole('ADMIN');
              this.isShowSaveAsDraft = false;
              this.isShowLeaveDialog = true;
              break;
            case 11:
              this.isReadOnly = !this.hasRole('ADMIN');
              this.isShowSaveAsDraft = false;
              this.isShowLeaveDialog = true;
              break;
            default:
              this.isReadOnly = true;
              this.isShowSaveAsDraft = false;
              this.isShowLeaveDialog = false;
              break;
          }
        }
      });
  }

  hasRole(role: string) {
    return this.permissionsService.hasRoles(role);
  }

  canDeactivate(currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState: RouterStateSnapshot):
    boolean | Observable<boolean> | Promise<boolean> {
    if (this.isShowLeaveDialog) {
      this.ams.showConfirm('Alert!', ' Are you sure you want to navigate away from this page? If you proceed, ALL of your changes will be lost. Press Yes to continue, or Cancel to stay on the current page.');
      return this.ams.confirm.afterClosed().pipe(map(result => {
        if (result === 'yes') {
          return true;
        } else {
          return false;
        }
      })
      );
    } else {
      return true;
    }
  }
  onAttachFileChange(event) {
    var self = this;
    let fileList = [];
    fileList = Array.from(event.target.files);
    if (fileList.length > 0) {

      if (fileList.length < 10) {
        let isPDFIndex = fileList.findIndex(item => item.type !== 'application/pdf');
        if (isPDFIndex <= -1) {
          const newLength = fileList.length;
          const uploadedLength = this.mgForm.value.attachment.length;
          if ((newLength + uploadedLength) <= 10) {

            let uploadFileSize: number = 0;
            let currentFileSize: number = 0;
            let totalFileSize = 0;
            for (let i = 0; i < newLength; i++) {
              uploadFileSize += parseInt(fileList[i].size);
            }
            if (uploadedLength > 0) {
              for (let i = 0; i < uploadedLength; i++) {
                currentFileSize += parseInt(this.mgForm.get(['attachment', i.toString(), 'attachSize']).value);
              }
            }
            totalFileSize = uploadFileSize + currentFileSize;
            if (totalFileSize < 10485760) {
              let current = fileList.map(function (item) { return item.name });
              if (this.mgForm.value.attachment.length > 0) {
                let upload = this.mgForm.value.attachment.map(function (item) {
                  return item.attachName
                });
                current = [...current, ...upload]
              }
              let isDuplicate = current.some(function (item, idx) {
                return current.indexOf(item) != idx
              });
              if (!isDuplicate) {
                let AttArray = this.mgForm.get('attachment') as FormArray;
                for (let i = 0; i < fileList.length; i++) {
                  const reader: any = new FileReader();
                  reader.onload = function (e) {
                    let base64 = [];
                    base64.push(btoa(e.target.result));
                    (<FormArray>self.mgForm.get('attachment')).push(self.createAttachment(base64[0], fileList[i].name, fileList[i].size));
                    self.ref.markForCheck();
                  };
                  reader.readAsBinaryString(fileList[i]);
                  this.ref.markForCheck();
                }
                this.fileInput1.nativeElement.value = '';

              } else {
                this.fileInput1.nativeElement.value = '';
                this.ams.showAlert('', 'Some of the files are already attached');
              }
            } else {
              this.fileInput1.nativeElement.value = '';
              this.ams.showAlert('', 'Attachment size exceeds the allowable limit, please remove some files');
            }

          } else {
            this.fileInput1.nativeElement.value = '';
            this.ams.showAlert('', 'Number of Attachments exceeds the allowable limit(10 files), please remove some files');
          }
        } else {
          this.fileInput1.nativeElement.value = '';
          this.ams.showAlert('', 'Only PDF files are allowed, please choose another file');
        }
      } else {
        this.fileInput1.nativeElement.value = '';
        this.ams.showAlert('', 'Number of Attachments exceeds the allowable limit(10 files), please remove some files');
      }
    }
    this.ref.markForCheck();
  }

  reseAttach() {
    let arrayAddAttach = this.mgForm.get('attachment') as FormArray;
    arrayAddAttach.controls.forEach((item, index) => {
      if (item.value.attachId !== -1) {
        item.value.deleteIt = 1;
        this.attachmentDeleted.push(item as FormControl);
      }
    });
    while (arrayAddAttach.length !== 0) {
      arrayAddAttach.removeAt(0);
    }
    this.fileInput1.nativeElement.value = '';
  }

  removeAttachFile(fileName) {
    let arrayAddAttach = this.mgForm.get('attachment') as FormArray;
    arrayAddAttach.controls.forEach((item, index) => {
      if (item.value.attachName === fileName) {
        arrayAddAttach.removeAt(index);
        if (item.value.attachId !== -1) {
          item.value.deleteIt = 1;
          this.attachmentDeleted.push(item as FormControl);
        }
      }
    });
  }

  onValidateEIN() {
    this.ams.showLoader('Verifying...');
    const EIN = this.mgForm.get(['OrgInfo', 'EIN']).value;
    this._dataService.CharityCheck(EIN).subscribe({
      next: data => {
        if (data) {
          this.isEINValidated = true;
          if (typeof data === "string" && data === "invalid") {
            this.ams.closeDialog();
            this.ams.showAlert('Invalid!', 'EIN: ' + EIN + ' is not valid !' + ' Please contact contributions@lge-ku.com');
          } else {
            this.ams.closeDialog();
            let orgInfoData: any = {};
            orgInfoData.orgInfoId = null;
            orgInfoData.webAddress = '';
            orgInfoData.name = (data.organization_name).toUpperCase();
            orgInfoData.EIN = data.ein;
            orgInfoData.payToOption = data.organization_name.length > 32 ? 'Yes' : 'No';
            orgInfoData.payTo = data.organization_name.length > 32 ? (data.organization_name.substring(0, 32).toUpperCase()) : '';
            orgInfoData.address1 = data.address_line1;
            orgInfoData.address2 = data.address_line2;
            orgInfoData.city = data.city;
            orgInfoData.state = data.state;
            orgInfoData.zipCode = data.zip;
            orgInfoData.subsection = data.subsection_501;
            this.mgForm.get('OrgInfo').setValue(orgInfoData);
          }
          this.ref.markForCheck();
        }
      },
      error: (error: Error) => {
        this.ams.closeDialog();
        this.ams.showAlert('Error!', `Please try again later! \n Details:${JSON.stringify(error)}`);
        this.ref.markForCheck();
      }
    })
  }

  searchEmpById() {
    let empData: any = {};
    empData.EmpId = this.mgForm.get(['DonorInfo', 'EmpId']).value;
    this.ams.showLoader("Searching...")
    this._dataService.getEmployeeByEmpId(empData).subscribe({
      next: data => {
        this.ams.closeDialog();
        this.ref.markForCheck();
        empData.donorInfoId = null;
        empData.title = data.Title;
        empData.department = data.DepartmentName;
        empData.firstName = data.FirstName;
        empData.lastName = data.LastName;
        empData.phone = data.Telephone;
        empData.empStatus = this.mgForm.get(['DonorInfo', 'empStatus']).value;
        empData.company = this.mgForm.get(['DonorInfo', 'company']).value;
        this.mgForm.get('DonorInfo').setValue(empData);
      },
      error:
        (error: Error) => {
          this.ams.closeDialog();
          if (error.message.includes('Invalid Employee ID'))
            this.ams.showAlert("Invalid!!!", "Your Employee ID input is invalid. Please use valid Employee ID.")
          else
            this.ams.showAlert('Error!', error.message);
          this.ref.markForCheck();
        }
    })
  }

  getTaxExemptStatus(value) {
    if (value === '')
      return 'Other';
    else if (isNaN(value) || +value === 0) {
      return value;
    } else {
      return `501(c)(${+value})`;
    }
  }
  DownloadAllAttachFile() {
    if (this.formId) {
      const arrayAddAttach = this.mgForm.get('attachment') as FormArray;
      arrayAddAttach.controls.forEach((item: any) => {
        this.DownloadAttachFile(item.value.attachName);
      });
    }
  }

  DownloadAttachFile(fileName) {
    const attachments = this.mgForm.get('attachment') as FormArray;
    const file = attachments.controls.find((item) => {
      return item.value.attachName === fileName;
    });
    if (file.value.attachId !== -1) {
      this._dataService.DownloadAttachmentFileById(file.value.attachId, 'MG').subscribe((response) => {
        saveAs(response, `${file.value.attachName}`);
        this.ams.closeDialog();
      });
    } else {
      this.ams.showAlert('Failed to download', `You must saved change(s) first to download ${fileName} file`);
    }
  }

  onSubmit(): void {
    if (this.mgForm.invalid) {
      this.ams.showAlert('Alert', 'Please complete all required field(s) marked with an *');
    } else if (this.mgForm.get('attachment').value.length === 0) {
      this.ams.showAlert('Alert', 'Please attach required PDF documents marked with an *');
    } else {
      this.mgForm.get('submittedDate').setValue(new Date());
      const arrayNewSubmit = [2, 7, 23, null];
      this.pendingMsg = "Saving...";
      if (arrayNewSubmit.includes(this.mgForm.value.statusId)) {
        this.mgForm.value.statusId = 1;
        this.pendingMsg = "Submitting...";
      }
      const userId = this.mgForm.value.userId === '' ? this.permissionsService.getUserId() : this.mgForm.value.userId;
      this.mgForm.value.userId = userId;
      if ((this.mgForm.value.attachment.some((item, idx) => this.mgForm.value.attachment.indexOf(item) != idx) === -1)) {
        this.mgForm.value.attachment.push(...this.attachmentDeleted.value);
      }
      this.store.dispatch(new MatchingGiftAction.IUDMatchingGiftState(this.mgForm.value, null));
    }
  }

  onSaveAsDraft(): void {
    this.mgForm.get('submittedDate').setValue(new Date());
    this.mgForm.value.statusId = 2;
    this.mgForm.value.userId = this.permissionsService.getUserId();
    if ((this.mgForm.value.attachment.some((item, idx) => this.mgForm.value.attachment.indexOf(item) != idx) === -1)) {
      this.mgForm.value.attachment.push(...this.attachmentDeleted.value);
    }
    this.pendingMsg = "Saving...";
    this.store.dispatch(new MatchingGiftAction.IUDMatchingGiftState(this.mgForm.value, null));
  }

  // Unsubscribe from state data changes
  private endSubscriptions(): void {
    this.pendingSubscription.unsubscribe();
    this.errorSubscription.unsubscribe();
    this.successSubscription.unsubscribe();
    this.stateDataSubscription.unsubscribe();
  }

  ngOnDestroy() {
    this.endSubscriptions();
    this.store.dispatch(new MatchingGiftAction.LoadInitialState())
  }

  onApproveRejectWithDrawn(action: string) {
    const data: any = {};
    data.formId = this.formData.FormMGLogInfo.formId;
    data.formLogId = this.formData.FormMGLogInfo.formLogId;
    data.empId = this.formData.FormMGLogInfo.empId;
    data.empName = this.formData.FormMGLogInfo.empName;
    data.action = action;
    data.orgName = this.formData.OrgInfo.name;
    data.status = this.formData.FormMGLogInfo.status;
    data.email = '';
    data.userId = this.formData.userId;
    data.empTypeForLog = this.formData.FormMGLogInfo.empTypeForLog;
    data.empTitle = this.formData.FormMGLogInfo.title;
    data.amount = this.formData.amount;
    this.ams.showApproveReject('Are you sure you want to Approve?', '');
    this.ams.approveReject.afterClosed().subscribe(result => {
      if (result) {
        data.comment = result.comment;
        switch (action) {
          case 'Approved':
            this.pendingMsg = "Approving...";
            break;
          case 'Rejected':
            this.pendingMsg = "Rejecting...";
            break;
          case 'Withdrawn':
            this.pendingMsg = "Withdrawing...";
            break;
        }
        this.store.dispatch(new MatchingGiftAction.IUDMatchingGiftState(this.mgForm.value, data));
      }
    });
  }

}